import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  MenuItem,
  Chip,
  OutlinedInput,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';

const engineerLabel = (engineer) =>
  `${engineer.firstName || ''} ${engineer.lastName || ''}`.trim() || engineer.email || engineer._id;

const MROActions = ({ open, onClose, workOrder, onSuccess }) => {
  const [decision, setDecision] = useState('ACCEPT');
  const [notes, setNotes] = useState('');
  const [assignedEngineerIds, setAssignedEngineerIds] = useState([]);
  const [currentEngineerId, setCurrentEngineerId] = useState('');
  const [assignedDate, setAssignedDate] = useState('');
  const [mroEngineers, setMroEngineers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();

  useEffect(() => {
    if (!open) return;
    let ignore = false;
    const loadUsers = async () => {
      try {
        const response = await axios.get('/api/users');
        const users = Array.isArray(response.data) ? response.data : [];
        const engineers = users.filter((u) => u?.role === 'MRO_ENGINEER');
        if (!ignore) setMroEngineers(engineers);
      } catch (err) {
        if (!ignore) setMroEngineers([]);
      }
    };
    loadUsers();
    return () => {
      ignore = true;
    };
  }, [open]);

  const handleTeamChange = (event) => {
    const value = typeof event.target.value === 'string'
      ? event.target.value.split(',')
      : event.target.value;
    setAssignedEngineerIds(value);
    if (currentEngineerId && !value.includes(currentEngineerId)) {
      setCurrentEngineerId(value[0] || '');
    } else if (!currentEngineerId && value.length) {
      setCurrentEngineerId(value[0]);
    }
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (!notes.trim()) {
        setError('Notes are required');
        return;
      }

      if (assignedEngineerIds.length && currentEngineerId && !assignedEngineerIds.includes(currentEngineerId)) {
        setError('Current shift engineer must be one of the assigned engineers');
        return;
      }

      const endpoint = decision === 'ACCEPT'
        ? `/api/maintenance/${workOrder._id}/mro/accept`
        : `/api/maintenance/${workOrder._id}/mro/reject`;

      await axios.post(endpoint, {
        notes: notes.trim(),
        assignedEngineerIds: assignedEngineerIds.length ? assignedEngineerIds : undefined,
        currentEngineerId: assignedEngineerIds.length
          ? (currentEngineerId || assignedEngineerIds[0])
          : undefined,
        assignedDate: assignedDate || undefined
      });

      showToast(`Work order ${decision === 'ACCEPT' ? 'accepted' : 'rejected'} successfully`, 'success');
      onSuccess();
      handleClose();
    } catch (err) {
      const errorMessage = err.response?.data?.message || `Failed to ${decision === 'ACCEPT' ? 'accept' : 'reject'} work order`;
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setDecision('ACCEPT');
    setNotes('');
    setAssignedEngineerIds([]);
    setCurrentEngineerId('');
    setAssignedDate('');
    setError('');
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>MRO Action</DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Work Order: <strong>{workOrder?.title}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Aircraft: <strong>{workOrder?.aircraftId?.registration || 'Unknown'}</strong>
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" gutterBottom>
            Decision
          </Typography>
          <ToggleButtonGroup
            value={decision}
            exclusive
            onChange={(e, newValue) => newValue && setDecision(newValue)}
            fullWidth
          >
            <ToggleButton value="ACCEPT">Accept</ToggleButton>
            <ToggleButton value="REJECT">Reject</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        <TextField
          fullWidth
          multiline
          rows={4}
          label="Notes (Required)"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder="Please provide notes for your decision..."
          required
          sx={{ mt: 2 }}
        />
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel id="mro-assign-engineers-label">Assign MRO Engineer(s) (Optional)</InputLabel>
          <Select
            labelId="mro-assign-engineers-label"
            multiple
            value={assignedEngineerIds}
            onChange={handleTeamChange}
            input={<OutlinedInput label="Assign MRO Engineer(s) (Optional)" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((id) => {
                  const eng = mroEngineers.find((e) => e._id === id);
                  return <Chip key={id} size="small" label={eng ? engineerLabel(eng) : id} />;
                })}
              </Box>
            )}
          >
            {mroEngineers.map((engineer) => (
              <MenuItem key={engineer._id} value={engineer._id}>
                {engineerLabel(engineer)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {assignedEngineerIds.length > 0 && (
          <TextField
            select
            fullWidth
            label="Current shift engineer"
            value={currentEngineerId || assignedEngineerIds[0] || ''}
            onChange={(e) => setCurrentEngineerId(e.target.value)}
            helperText={
              assignedEngineerIds.length >= 2
                ? 'Assigning 2+ engineers locks handover to this team. One engineer is enough — they can hand over to anyone later.'
                : 'Optional second engineer is not required. This engineer can hand over to any MRO engineer at shift change.'
            }
            sx={{ mt: 2 }}
          >
            {assignedEngineerIds.map((id) => {
              const eng = mroEngineers.find((e) => e._id === id);
              return (
                <MenuItem key={id} value={id}>
                  {eng ? engineerLabel(eng) : id}
                </MenuItem>
              );
            })}
          </TextField>
        )}
        <TextField
          fullWidth
          type="date"
          label="Assign Date (Optional)"
          value={assignedDate}
          onChange={(e) => setAssignedDate(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !notes.trim()}
          color={decision === 'REJECT' ? 'error' : 'primary'}
        >
          {loading ? 'Processing...' : decision === 'ACCEPT' ? 'Accept' : 'Reject'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MROActions;
